import type { CSSProperties } from 'react';

/**
 * Shared, theme-aware colour tokens for the DataTableV2 grid surface, so nothing
 * re-hardcodes the header-shade fill, the zebra-stripe fill or the sort-accent
 * hue.
 *
 * Three values, one set per resolved theme:
 * - `headerShade` — the header band fill (and the pivot's pinned label column /
 *   total-row rail). AG-Grid's `themeQuartz.withParams` takes JS colour VALUES,
 *   not CSS variables, so `createThemedGridConfig` feeds this literal to
 *   `headerBackgroundColor` rather than a `--om-*` token.
 * - `stripeShade` — the zebra fill of every ODD body row when a consumer opts
 *   into `striped`. Deliberately the SAME value as `headerShade` (neutral08
 *   `#F9FAFB` light / `#1F1F1F` dark, the pre-AG-Grid flat table's stripe), so
 *   the shaded header, the pivot's shaded rails and the stripes all read as one
 *   tint instead of three near-misses. Kept as its own field because the two are
 *   separate design decisions and either may move independently.
 * - `sortAccent` — the active sort-arrow / order-digit colour on the NATIVE
 *   header. The flat Mantine table's "Mantine blue" (`#228be6` / `#4dabf7`); it
 *   is NOT a brand token (no `--om-*` / `--dash-*` maps to it), and the native
 *   header icons are inline SVG strings that read it through a CSS variable.
 *
 * No existing `--om-*` / `--dash-*` token maps to either pair (checked
 * `web-ui/app/globals.scss`): the accent is Mantine's blue and the shade is a
 * grid-only band with no semantic token. Both are literal hexes keyed off
 * `resolvedTheme` (next-themes), matching the rest of the app's dark/light flip.
 * An earlier commit deleted an unused `resolveGridHeaderShade`; this replaces it
 * and is actually consumed (see `createThemedGridConfig` + `gridNativeHeader`).
 */
export interface GridThemeColors {
  /** Header band / sticky-rail fill. */
  headerShade: string;
  /** Odd-row zebra fill, used only when the consumer opts into `striped`. */
  stripeShade: string;
  /** Active sort-arrow + multi-sort order-digit colour. */
  sortAccent: string;
}

export const GRID_THEME_COLORS: { light: GridThemeColors; dark: GridThemeColors } = {
  light: { headerShade: '#F9FAFB', stripeShade: '#F9FAFB', sortAccent: '#228be6' },
  dark: { headerShade: '#1F1F1F', stripeShade: '#1F1F1F', sortAccent: '#4dabf7' },
};

/** The grid's header-shade / stripe / sort-accent set for the active (resolved) theme. */
export const resolveGridThemeColors = (resolvedTheme: string | undefined): GridThemeColors =>
  resolvedTheme === 'dark' ? GRID_THEME_COLORS.dark : GRID_THEME_COLORS.light;

/**
 * CSS custom property the native header's inline-SVG sort icons (and the
 * `.ag-sort-order` digit) read their accent colour from. Set it on the grid
 * wrapper via `gridThemeCssVars` so a live light/dark toggle re-colours the
 * icons without re-rendering them.
 */
export const GRID_SORT_ACCENT_CSS_VAR = '--grid-sort-accent';

/**
 * Inline style exposing the resolved sort accent as `--grid-sort-accent` on the
 * grid wrapper. The module-level icon SVGs reference `var(--grid-sort-accent)`,
 * so the colour follows the theme without re-creating the icon strings.
 */
export const gridThemeCssVars = (resolvedTheme: string | undefined): CSSProperties =>
  ({ [GRID_SORT_ACCENT_CSS_VAR]: resolveGridThemeColors(resolvedTheme).sortAccent }) as CSSProperties;
